import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import { useGetMemberListQuery } from '@/api/member-management/admissionRequest';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import AppSummarySearchBar from '@/common/components/form-inputs/AppSummarySearchBar';
import { useNavigate } from 'react-router-dom';
import MemberStatusChip from '@/common/components/MemberStatusChip';

interface Props {
	getSelectMemberRow: any;
}

const SelectMemberEnDialog = (props: Props) => {
	const { getSelectMemberRow } = props;
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const showModal = useModal();
	const [selectedRowId, setSelectedRowId] = useState<number | string | null>(
		null
	);
	const [selectedRow, setSelectedRow] = useState<any>({});
	const navigate = useNavigate();

	const defaultValues: DefaultValues<any> = {};
	const methods = useForm<any>({
		defaultValues,
	});
	const watchSearchString = methods.watch('searchString');
	const { data: memberList } = useGetMemberListQuery(
		{
			serviceFollowList: [0, 1, 2],
			type: 0,
			sortings: [],
			pagination: { page: 1, size: 99999 },
			searchString: methods.getValues('searchString'),
		},
		{
			refetchOnMountOrArgChange: true,
			skip: !open,
		}
	);

	const handleClose = () => {
		setOpen(false);
	};

	const onSubmit = async (data: any) => {
		// const parts = nextFileNo?.data.split('/');
		// const response = await registerCase({
		// 	...data,
		// 	admissionDate: data.admissionDate,
		// 	dateOfCaseOpen: data.dateOfCaseOpen,
		// 	id: admissionRequestId,
		// 	fileNo: `${parts[0]}/${parts[1]}/${data.fileNoB}/${data.fileNoC}`,
		// });
		// if ('data' in response) {
		// 	enqueueSnackbar(t('snackbar.updateMemberSuccess'), {
		// 		variant: 'success',
		// 		anchorOrigin: { horizontal: 'center', vertical: 'top' },
		// 		autoHideDuration: 3000,
		// 		action: (key) => {
		// 			return <CloseIcon onClick={() => closeSnackbar(key)} />;
		// 		},
		// 	});
		// 	handleClose();
		// }
		if (!selectedRowId) return;
		getSelectMemberRow(selectedRow);
		handleClose();
	};

	const handleDialog = async () => {
		setOpen(true);
	};

	const columns: GridColDef[] = [
		{
			field: 'fileNo',
			headerName: t('member.fileNo'),
			flex: 15,
		},
		{
			field: 'nameEn',
			headerName: t('member.name'),
			flex: 15,
			renderCell: (params) => (
				<Box>
					<Typography variant='body2'>{params.row.nameEn}</Typography>
					<Typography variant='body2'>{params.row.nameZh}</Typography>
				</Box>
			),
		},
		{
			field: 'phone',
			headerName: t('member.mobilePhone'),
			flex: 25,
		},

		{
			field: 'serviceFollowDesc',
			headerName: t('member.serviceFollow'),
			flex: 15,
		},
		{
			field: 'membershipStateDesc',
			headerName: t('member.membershipState'),
			flex: 15,
		},
		{
			field: 'status',
			headerName: t('member.status'),
			flex: 15,
			maxWidth: 140,
			minWidth: 140,
			renderCell: (params) => (
				<MemberStatusChip
					status={params.row.status}
					label={params.row.statusDesc}
				/>
			),
		},
		{
			field: 'checkbox',
			headerName: '',
			flex: 15,
			renderCell: (params) => (
				<Checkbox
					checked={params.row.id === selectedRowId}
					onChange={() => {
						// Toggle: clicking the already-selected row unchecks it.
						setSelectedRowId(
							params.row.id === selectedRowId ? null : params.row.id
						);
						setSelectedRow(params.row.id === selectedRowId ? {} : params.row);
					}}
				/>
			),
		},
	];

	return (
		<>
			<Button
				variant='contained'
				onClick={() => {
					handleDialog();
				}}
			>
				{t('family.reSelectMembership')}
			</Button>
			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
				onKeyDown={(e) => {
					if (e.key === 'Tab') {
						e.stopPropagation();
					}
				}}
			>
				<DialogTitle variant='h5'>
					{t('family.selectPersonRecovery')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<Typography variant='body2' sx={{ marginBottom: '12px' }}>
						{t('family.selectPersonRecoveryText')}
					</Typography>
					<FormProvider {...methods}>
						<Grid
							container
							spacing={2}
							marginTop={0}
							marginBottom={2}
							alignItems='center'
						>
							<Grid size={12}>
								<AppSummarySearchBar
									name='searchString'
									control={methods.control}
									placeholder={t('common.search')}
								/>
							</Grid>
							<Grid size={12} display='flex'>
								<LocalDataGrid
									rows={memberList?.data.items || []}
									columns={columns}
									getRowId={(row) => row.id}
									pagination={false}
								/>
							</Grid>
						</Grid>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button sx={{ textTransform: 'none' }} onClick={() => handleClose()}>
						{t('button.cancel')}
					</Button>
					<Button
						type='submit'
						variant='contained'
						disabled={!selectedRowId}
						onClick={onSubmit}
					>
						{t('button.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default SelectMemberEnDialog;
